import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search } from 'lucide-react';
import { useTranslations } from '../translations';
import { searchReportedProducts, updateAdminProductStatus, getReportedProductReasons } from '../services/admin.api';
import { Category, Location, UserProduct } from '../services/api.types';
import { fetchCategories } from '../services/category.api';
import { searchLocations } from '../services/location.api';
import { getAccessToken } from '../services/auth';
import AdminPagination from '../components/AdminPagination';
import AdminCard from '../components/AdminCard';
import { formatAdminProductDate, formatAdminProductPrice } from '../utils/adminProductDisplay';
import ThemedSelect from '../components/ThemedSelect';
import ConfirmModal from '../components/ConfirmModal';
import RejectionModal from '../components/RejectionModal';
import ReportReasonsModal from '../components/ReportReasonsModal';
import { useNotification } from '../components/NotificationProvider';
import './AdminReportedProducts.css';

interface AdminReportedProductsProps {
  onNavigateToProductDetail: (id: string) => void;
}

type AdminReportedProductSortOption = 'NEWEST' | 'OLDEST' | 'PRICE_ASC' | 'PRICE_DESC';
type NegotiationFilter = '' | 'NEGOTIABLE' | 'NON_NEGOTIABLE';

const AdminReportedProducts: React.FC<AdminReportedProductsProps> = ({
  onNavigateToProductDetail,
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [productType, setProductType] = useState<'PRODUCT' | 'DEMAND'>('PRODUCT');
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [distance, setDistance] = useState('');
  const [locationOptions, setLocationOptions] = useState<Location[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [negotiationFilter, setNegotiationFilter] = useState<NegotiationFilter>('');
  const [seller, setSeller] = useState('');
  const [sortOption, setSortOption] = useState<AdminReportedProductSortOption>('NEWEST');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const [approveModal, setApproveModal] = useState<{
    show: boolean;
    productId: string | null;
  }>({ show: false, productId: null });

  const [rejectModal, setRejectModal] = useState<{
    show: boolean;
    productId: string | null;
    productTitle: string;
  }>({ show: false, productId: null, productTitle: '' });

  const [reportReasonsModal, setReportReasonsModal] = useState<{
    show: boolean;
    productId: string;
    productTitle: string;
    reasons: string[];
  }>({ show: false, productId: '', productTitle: '', reasons: [] });

  const selectedCategory = categories.find(category => category.id === selectedCategoryId);

  const getSortParams = () => {
    switch (sortOption) {
      case 'OLDEST':
        return { sortField: 'CREATED_AT', order: 'ASC' as const };
      case 'PRICE_ASC':
        return { sortField: 'PRICE', order: 'ASC' as const };
      case 'PRICE_DESC':
        return { sortField: 'PRICE', order: 'DESC' as const };
      case 'NEWEST':
      default:
        return { sortField: 'CREATED_AT', order: 'DESC' as const };
    }
  };

  const getSortLabel = (option: AdminReportedProductSortOption) => {
    switch (option) {
      case 'OLDEST':
        return t.searchResults.oldest;
      case 'PRICE_ASC':
        return t.searchResults.priceAscending;
      case 'PRICE_DESC':
        return t.searchResults.priceDescending;
      case 'NEWEST':
      default:
        return t.searchResults.newest;
    }
  };

  const sortOptions: AdminReportedProductSortOption[] = ['NEWEST', 'OLDEST', 'PRICE_ASC', 'PRICE_DESC'];

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data.content || []);
      } catch (err) {
        console.error('Error loading reported product categories:', err);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [currentPage, productType, sortOption]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(target)) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchLocationOptions = async () => {
      const searchTerm = location.trim();
      if (!showLocationDropdown || selectedLocationId || searchTerm.length < 2) {
        if (searchTerm.length < 2) {
          setLocationOptions([]);
        }
        return;
      }

      setLocationLoading(true);
      try {
        const response = await searchLocations(1, 20, null, searchTerm, getAccessToken());
        setLocationOptions(response.locations.content || []);
      } catch (err) {
        console.error('Error searching reported product locations:', err);
        setLocationOptions([]);
      } finally {
        setLocationLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchLocationOptions, 300);
    return () => clearTimeout(debounceTimer);
  }, [location, selectedLocationId, showLocationDropdown]);

  const loadProducts = async (pageToLoad = currentPage) => {
    setLoading(true);
    setError(null);

    try {
      const minPriceNum = minPrice ? parseFloat(minPrice) : null;
      const maxPriceNum = maxPrice ? parseFloat(maxPrice) : null;
      const { sortField, order } = getSortParams();
      const locationParam = selectedLocationId
        ? {
            ids: [selectedLocationId],
            ...(Number(distance) > 0 ? {
              range: {
                range: Number(distance)
              }
            } : {})
          }
        : undefined;

      const response = await searchReportedProducts({
        page: pageToLoad,
        size: 20,
        productType,
        productStatus: 'ACTIVE',
        sortField,
        order,
        name: searchQuery || null,
        location: locationParam,
        categoryId: selectedCategoryId || null,
        subCategoryIds: selectedSubCategoryId ? [selectedSubCategoryId] : [],
        minPrice: minPriceNum,
        maxPrice: maxPriceNum,
        negotiationType: negotiationFilter || null,
        username: seller || null,
      });

      console.log('Reported products loaded:', response);
      setProducts(response.content || []);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      setError(t.administration.products.errorLoading);
      console.error('Error loading reported products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    if (currentPage === 1) {
      loadProducts(1);
    } else {
      setCurrentPage(1);
    }
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setLocation('');
    setSelectedLocationId('');
    setDistance('');
    setLocationOptions([]);
    setSelectedCategoryId('');
    setSelectedSubCategoryId('');
    setMinPrice('');
    setMaxPrice('');
    setNegotiationFilter('');
    setSeller('');
    setSortOption('NEWEST');
    if (currentPage === 1) {
      setTimeout(() => loadProducts(1), 0);
    } else {
      setCurrentPage(1);
    }
  };

  const handleLocationInputChange = (value: string) => {
    setLocation(value);
    setSelectedLocationId('');
    setDistance('');
    setShowLocationDropdown(true);
  };

  const handleLocationSelect = (loc: Location) => {
    setLocation(`${loc.diacriticsName}, ${loc.county.name}`);
    setSelectedLocationId(loc.id);
    setShowLocationDropdown(false);
    setLocationOptions([]);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId('');
  };

  const handleProductClick = async (productId: string, productTitle: string) => {
    try {
      const reportData = await getReportedProductReasons(productId);
      setReportReasonsModal({
        show: true,
        productId,
        productTitle,
        reasons: reportData.reportedProductReasons,
      });
    } catch (err) {
      console.error('Error loading report reasons:', err);
      showNotification('Nu am putut încărca motivele de raportare.', 'error');
    }
  };

  const handleApprove = (productId: string) => {
    setApproveModal({ show: true, productId });
  };

  const handleDecline = (productId: string, productTitle: string) => {
    setRejectModal({ show: true, productId, productTitle });
  };

  const confirmApprove = async () => {
    if (!approveModal.productId) return;

    try {
      await updateAdminProductStatus(approveModal.productId, 'ACTIVE', []);

      setApproveModal({ show: false, productId: null });
      showNotification(t.administration.products.approveSuccess, 'success');
      loadProducts();
    } catch (err) {
      console.error('Error approving product:', err);
      showNotification(t.administration.products.errorUpdating, 'error');
    }
  };

  const confirmReject = async (reasons: string[]) => {
    if (!rejectModal.productId) return;

    try {
      await updateAdminProductStatus(rejectModal.productId, 'DECLINED', reasons);

      setRejectModal({ show: false, productId: null, productTitle: '' });
      showNotification(t.administration.products.declineSuccess, 'success');
      loadProducts();
    } catch (err) {
      console.error('Error rejecting product:', err);
      showNotification(t.administration.products.errorUpdating, 'error');
    }
  };

  return (
    <div className="admin-reported-products">
      <div className="admin-reported-products-search-bar">
        <div className="admin-reported-products-search-control">
          <Search className="admin-reported-products-search-icon" aria-hidden />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleApplyFilters()}
            placeholder={t.home.searchPlaceholder}
            aria-label={t.home.searchPlaceholder}
          />
        </div>

        <div
          className="admin-reported-products-search-control admin-reported-products-location-control"
          ref={locationDropdownRef}
        >
          <MapPin className="admin-reported-products-search-icon" aria-hidden />
          <input
            type="text"
            value={location}
            onChange={(event) => handleLocationInputChange(event.target.value)}
            onFocus={() => setShowLocationDropdown(true)}
            onKeyDown={(event) => event.key === 'Enter' && handleApplyFilters()}
            placeholder={t.searchResults.allCountry}
            aria-label={t.createProduct.locationLabel}
          />
          {showLocationDropdown && (locationLoading || locationOptions.length > 0 || location.trim().length >= 2) && (
            <div className="admin-reported-products-dropdown-menu">
              {locationLoading ? (
                <div className="admin-reported-products-dropdown-empty">{t.common.loading}</div>
              ) : locationOptions.length > 0 ? (
                locationOptions.map(loc => (
                  <button
                    key={loc.id}
                    type="button"
                    className="admin-reported-products-dropdown-option"
                    onClick={() => handleLocationSelect(loc)}
                  >
                    <span>{loc.diacriticsName}</span>
                    <small>{loc.county.name}</small>
                  </button>
                ))
              ) : (
                <div className="admin-reported-products-dropdown-empty">{t.common.noResults}</div>
              )}
            </div>
          )}
        </div>

        <div className="admin-reported-products-distance-control">
          <input
            type="number"
            min="0"
            value={distance}
            onChange={(event) => setDistance(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleApplyFilters()}
            placeholder={t.searchResults.distance}
            aria-label={t.searchResults.distance}
            disabled={!selectedLocationId}
          />
          <span>km</span>
        </div>

        <button
          type="button"
          className="admin-reported-products-search-button"
          onClick={handleApplyFilters}
        >
          {t.searchResults.searchButton}
        </button>
      </div>

      <div className="admin-reported-products-results-layout">
        <aside className="admin-reported-products-filters">
          <h2 className="admin-reported-products-filters-title">{t.searchResults.filters}</h2>
        <div className="admin-reported-products-filter-row">
          <div className="admin-reported-products-filter-field">
            <label className="admin-reported-products-filter-label">Tip</label>
            <div className="admin-reported-products-type-toggle">
              <button
                type="button"
                className={`admin-reported-products-type-button ${productType === 'PRODUCT' ? 'admin-reported-products-type-button-active' : ''}`}
                onClick={() => {
                  setProductType('PRODUCT');
                  setCurrentPage(1);
                }}
              >
                {t.searchResults.tabSell}
              </button>
              <button
                type="button"
                className={`admin-reported-products-type-button ${productType === 'DEMAND' ? 'admin-reported-products-type-button-active' : ''}`}
                onClick={() => {
                  setProductType('DEMAND');
                  setCurrentPage(1);
                }}
              >
                {t.searchResults.tabBuy}
              </button>
            </div>
          </div>

          <div className="admin-reported-products-filter-field">
            <label className="admin-reported-products-filter-label">{t.searchResults.orderBy}</label>
            <select
              className="admin-reported-products-filter-input"
              value={sortOption}
              onChange={(e) => {
                setSortOption(e.target.value as AdminReportedProductSortOption);
                setCurrentPage(1);
              }}
            >
              {sortOptions.map(option => (
                <option key={option} value={option}>
                  {getSortLabel(option)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="admin-reported-products-filter-row">
          <div className="admin-reported-products-filter-field">
            <label className="admin-reported-products-filter-label">{t.searchResults.filterCategory}</label>
            <ThemedSelect
              value={selectedCategoryId}
              options={[
                { value: '', label: t.searchResults.allCategories },
                ...categories.map(category => ({ value: category.id, label: category.name })),
              ]}
              onChange={handleCategorySelect}
              ariaLabel={t.searchResults.filterCategory}
            />
          </div>

          <div className="admin-reported-products-filter-field">
            <label className="admin-reported-products-filter-label">{t.searchResults.filterSubcategory}</label>
            <ThemedSelect
              key={selectedCategoryId}
              value={selectedSubCategoryId}
              options={[
                { value: '', label: t.searchResults.allSubcategories },
                ...(selectedCategory?.subCategories ?? []).map(subCategory => ({ value: subCategory.id, label: subCategory.name })),
              ]}
              onChange={setSelectedSubCategoryId}
              ariaLabel={t.searchResults.filterSubcategory}
              disabled={!selectedCategory?.subCategories.length}
            />
          </div>
        </div>

        <div className="admin-reported-products-filter-row">
          <div className="admin-reported-products-filter-field admin-reported-products-filter-field-small">
            <label className="admin-reported-products-filter-label">{t.searchResults.filterPriceMin}</label>
            <input
              className="admin-reported-products-filter-input"
              type="number"
              min="0"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder={t.searchResults.filterPriceMin}
            />
          </div>

          <div className="admin-reported-products-filter-field admin-reported-products-filter-field-small">
            <label className="admin-reported-products-filter-label">{t.searchResults.filterPriceMax}</label>
            <input
              className="admin-reported-products-filter-input"
              type="number"
              min="0"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder={t.searchResults.filterPriceMax}
            />
          </div>

          <div className="admin-reported-products-filter-field">
            <label className="admin-reported-products-filter-label">{t.searchResults.filterNegotiable}</label>
            <select
              className="admin-reported-products-filter-input"
              value={negotiationFilter}
              onChange={(e) => setNegotiationFilter(e.target.value as NegotiationFilter)}
            >
              <option value="">{t.common.all}</option>
              <option value="NEGOTIABLE">{t.productDetail.negotiable}</option>
              <option value="NON_NEGOTIABLE">{t.productDetail.nonNegotiable}</option>
            </select>
          </div>

          <div className="admin-reported-products-filter-field admin-reported-products-filter-field-wide">
            <label className="admin-reported-products-filter-label">{t.searchResults.filterSeller}</label>
            <input
              className="admin-reported-products-filter-input"
              type="text"
              value={seller}
              onChange={(e) => setSeller(e.target.value)}
              placeholder={t.searchResults.sellerPlaceholder}
            />
          </div>

          <div className="admin-reported-products-filter-actions">
            <button
              type="button"
              className="admin-reported-products-filter-apply"
              onClick={handleApplyFilters}
            >
              {t.searchResults.applyFilters}
            </button>
            <button
              type="button"
              className="admin-reported-products-filter-reset"
              onClick={handleResetFilters}
            >
              {t.searchResults.resetFilters}
            </button>
          </div>
        </div>
        </aside>

        <div className="admin-reported-products-results">
          {loading ? (
            <div className="admin-reported-products-loading">{t.common.loading}</div>
          ) : error ? (
            <div className="admin-reported-products-error">{error}</div>
          ) : products.length === 0 ? (
            <div className="admin-reported-products-empty">{t.administration.products.noProducts}</div>
          ) : (
            <div className="admin-reported-products-grid">
              {products.map((product) => {
                try {
                  return (
                    <div key={product.id} className="admin-reported-product-item">
                      <AdminCard
                        id={product.id}
                        title={product.title || ''}
                        description={product.description}
                        imageUrl={product.image?.thumbnailUrl || product.image?.imageUrl || ''}
                        location={product.locationDto?.name || ''}
                        datePosted={formatAdminProductDate(product.createdAt, t.productDetail)}
                        views={product.views || 0}
                        price={formatAdminProductPrice(product)}
                        negotiable={product.negotiable}
                        category={product.category?.name || ''}
                        subCategory={product.subCategory?.name || null}
                        status={product.status === 'ACTIVE' ? 'ACTIVE' : 'EXPIRED'}
                        suspensionReason={product.suspensionReason}
                        onClick={() => handleProductClick(product.id, product.title || '')}
                        onApprove={() => handleApprove(product.id)}
                        onDecline={() => handleDecline(product.id, product.title || '')}
                        currentTab="ACTIVE"
                        complaints={product.complaints}
                      />
                    </div>
                  );
                } catch (err) {
                  console.error('Error rendering product:', product.id, err);
                  return null;
                }
              })}
            </div>
          )}

          <AdminPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {approveModal.show && (
        <ConfirmModal
          title={t.administration.products.approve}
          message="Sigur vrei să aprobi acest produs?"
          onConfirm={confirmApprove}
          onCancel={() => setApproveModal({ show: false, productId: null })}
        />
      )}

      {rejectModal.show && (
        <RejectionModal
          productTitle={rejectModal.productTitle}
          onConfirm={confirmReject}
          onCancel={() => setRejectModal({ show: false, productId: null, productTitle: '' })}
        />
      )}

      {reportReasonsModal.show && (
        <ReportReasonsModal
          productTitle={reportReasonsModal.productTitle}
          reasons={reportReasonsModal.reasons}
          onClose={() => setReportReasonsModal({ show: false, productId: '', productTitle: '', reasons: [] })}
          onDecline={async () => {
            try {
              await updateAdminProductStatus(reportReasonsModal.productId, 'DECLINED', reportReasonsModal.reasons);
              setReportReasonsModal({ show: false, productId: '', productTitle: '', reasons: [] });
              showNotification(t.administration.products.declineSuccess, 'success');
              loadProducts();
            } catch (err) {
              console.error('Error declining product:', err);
              showNotification(t.administration.products.errorUpdating, 'error');
            }
          }}
        />
      )}
    </div>
  );
};

export default AdminReportedProducts;
